import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import "../styles/Reportes.css";

const TZ = "America/Bogota";

const PERIODOS = [
  { id: "dia", etiqueta: "Día" },
  { id: "semana", etiqueta: "Semana" },
  { id: "mes", etiqueta: "Mes" },
  { id: "anio", etiqueta: "Año" },
];

// Paleta categorica validada (separacion CVD y contraste verificados).
const COLOR_METODO = {
  Efectivo: "#1E8A4C",
  Nequi: "#E0A020",
  Daviplata: "#2E6FB7",
  Transferencia: "#B2542F",
};

const dinero = (n) => `$${Number(n || 0).toLocaleString("es-CO")}`;
const numero = (n) => Number(n || 0).toLocaleString("es-CO");

// Fecha civil de hoy en Bogota, como 'YYYY-MM-DD'.
function hoyEnBogota() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit",
  }).format(new Date());
}

const aISO = (t) =>
  `${t.getUTCFullYear()}-${String(t.getUTCMonth() + 1).padStart(2, "0")}-${String(
    t.getUTCDate()
  ).padStart(2, "0")}`;

// Aritmetica de calendario sobre 'YYYY-MM-DD'. No interviene ninguna zona
// horaria: se mueve el dia civil y el backend resuelve el rango real.
// Mes y anio se anclan al dia 1 para evitar el clasico salto de 31 de enero.
function desplazar(iso, periodo, pasos) {
  const [y, m, d] = iso.split("-").map(Number);
  if (periodo === "dia") return aISO(new Date(Date.UTC(y, m - 1, d + pasos)));
  if (periodo === "semana") return aISO(new Date(Date.UTC(y, m - 1, d + pasos * 7)));
  if (periodo === "mes") return aISO(new Date(Date.UTC(y, m - 1 + pasos, 1)));
  return aISO(new Date(Date.UTC(y + pasos, 0, 1)));
}

/* ------------------------------------------------------------------ barras */

function Barras({ filas, vacio }) {
  const max = Math.max(...filas.map((f) => f.valor), 0);

  if (!filas.length) return <p className="vacio">{vacio}</p>;

  return (
    <ul className="barras">
      {filas.map((f) => (
        <li key={f.clave} className="barra">
          <span className="barra-nombre" title={f.nombre}>{f.nombre}</span>
          <span className="barra-pista">
            <span
              className="barra-relleno"
              style={{
                width: max > 0 ? `${(f.valor / max) * 100}%` : "0%",
                backgroundColor: f.color,
              }}
            />
          </span>
          <span className="barra-valor">{f.texto}</span>
        </li>
      ))}
    </ul>
  );
}

/* ----------------------------------------------------------------- pantalla */

const Reportes = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const hoy = useMemo(() => hoyEnBogota(), []);

  const [periodo, setPeriodo] = useState("dia");
  const [ancla, setAncla] = useState(hoy);
  const [desde, setDesde] = useState(hoy);
  const [hasta, setHasta] = useState(hoy);
  const [tipo, setTipo] = useState("producto");

  const [ventas, setVentas] = useState(null);
  const [ranking, setRanking] = useState([]);
  const [rango, setRango] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  const esRangoLibre = periodo === "rango";

  const consulta = useMemo(
    () =>
      esRangoLibre
        ? `desde=${desde}&hasta=${hasta}`
        : `period=${periodo}&fecha=${ancla}`,
    [esRangoLibre, desde, hasta, periodo, ancla]
  );

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const [rVentas, rRanking] = await Promise.all([
        axios.get(`${apiUrl}/api/reportes/total-sales?${consulta}`),
        axios.get(`${apiUrl}/api/reportes/best-selling?type=${tipo}&${consulta}`),
      ]);

      setVentas(rVentas.data);
      setRango(rVentas.data?.rango ?? null);

      // Tolera la forma antigua (arreglo plano) por si el backend va atrasado.
      const datos = rRanking.data;
      setRanking(Array.isArray(datos) ? datos : datos?.items ?? []);
    } catch (e) {
      const mensaje =
        e.response?.data?.error ?? "No se pudieron cargar los reportes.";
      setError(mensaje);
      setVentas(null);
      setRanking([]);
    } finally {
      setCargando(false);
    }
  }, [apiUrl, consulta, tipo]);

  useEffect(() => { cargar(); }, [cargar]);

  const irA = (pasos) => setAncla((a) => desplazar(a, periodo, pasos));

  const cambiarPeriodo = (id) => {
    setPeriodo(id);
    if (id !== "rango") setAncla(hoy);
  };

  // El periodo mostrado ya alcanza el presente: no hay nada mas adelante.
  const enElPresente = !rango || rango.hasta >= hoy;

  const totales = ventas?.totales ?? { monto: 0, ventas: 0 };
  const promedio = totales.ventas > 0 ? Math.round(totales.monto / totales.ventas) : 0;

  const filasMetodos = (ventas?.metodos ?? []).map((m) => ({
    clave: m.metodo,
    nombre: m.metodo,
    valor: m.monto,
    texto: dinero(m.monto),
    color: COLOR_METODO[m.metodo] ?? "var(--ink-400)",
  }));

  const filasRanking = ranking.map((i) => ({
    clave: `${i.id}-${i.name}`,
    nombre: i.name,
    valor: Number(i.cantidad_vendida),
    texto: numero(i.cantidad_vendida),
    color: "var(--brand-600)",
  }));

  return (
    <div className="reportes">
      {/* ---------------------------------------------- selector de periodo */}
      <header className="periodo">
        <div className="periodo-chips" role="tablist" aria-label="Período del reporte">
          {PERIODOS.map((p) => (
            <button
              key={p.id}
              role="tab"
              aria-selected={periodo === p.id}
              className={`chip ${periodo === p.id ? "activo" : ""}`}
              onClick={() => cambiarPeriodo(p.id)}
            >
              {p.etiqueta}
            </button>
          ))}
          <button
            role="tab"
            aria-selected={esRangoLibre}
            className={`chip ${esRangoLibre ? "activo" : ""}`}
            onClick={() => cambiarPeriodo("rango")}
          >
            Rango
          </button>
        </div>

        {esRangoLibre ? (
          <div className="rango-libre">
            <label htmlFor="rango-desde">Desde</label>
            <input
              id="rango-desde" type="date" value={desde} max={hasta}
              onChange={(e) => setDesde(e.target.value)}
            />
            <label htmlFor="rango-hasta">Hasta</label>
            <input
              id="rango-hasta" type="date" value={hasta} min={desde}
              onChange={(e) => setHasta(e.target.value)}
            />
          </div>
        ) : (
          <div className="periodo-nav">
            <button
              className="flecha" onClick={() => irA(-1)}
              aria-label={`Período anterior`}
            >
              &lsaquo;
            </button>
            <h1 className="periodo-titulo">
              {rango?.etiqueta ?? "\u00a0"}
            </h1>
            <button
              className="flecha" onClick={() => irA(1)}
              disabled={enElPresente}
              aria-label={`Período siguiente`}
            >
              &rsaquo;
            </button>
            <button
              className="hoy" onClick={() => setAncla(hoy)}
              disabled={enElPresente}
            >
              Hoy
            </button>
          </div>
        )}
      </header>

      {error && <p className="error" role="alert">{error}</p>}

      <div className={`contenido ${cargando ? "cargando" : ""}`} aria-busy={cargando}>
        {/* ------------------------------------------------------- resumen */}
        <section className="tarjeta">
          <div className="hero">
            <span className="hero-etiqueta">Total vendido</span>
            <strong className="hero-monto">{dinero(totales.monto)}</strong>
            <span className="hero-detalle">
              {totales.ventas === 0
                ? "Sin ventas en este período"
                : `${numero(totales.ventas)} ${totales.ventas === 1 ? "venta" : "ventas"} · promedio ${dinero(promedio)}`}
            </span>
          </div>

          <h2 className="titulo-seccion">Por método de pago</h2>
          <Barras filas={filasMetodos} vacio="Sin datos de métodos de pago." />
        </section>

        {/* ------------------------------------------------------- ranking */}
        <section className="tarjeta">
          <div className="encabezado-seccion">
            <h2 className="titulo-seccion">Más vendidos</h2>
            <div className="tipo-chips">
              <button
                className={`chip pequeno ${tipo === "producto" ? "activo" : ""}`}
                onClick={() => setTipo("producto")}
                aria-pressed={tipo === "producto"}
              >
                Productos
              </button>
              <button
                className={`chip pequeno ${tipo === "bebida" ? "activo" : ""}`}
                onClick={() => setTipo("bebida")}
                aria-pressed={tipo === "bebida"}
              >
                Bebidas
              </button>
            </div>
          </div>
          <Barras
            filas={filasRanking}
            vacio={`No hubo ${tipo === "producto" ? "productos" : "bebidas"} vendidas en este período.`}
          />
        </section>
      </div>
    </div>
  );
};

export default Reportes;
